"use client";

import * as React from "react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { icons } from "../../assets/icons";
import { recuperarClavePaso1 } from "../../api/usuarioServices";
import Swal from "sweetalert2";

export default function RecoverPasswordStep1() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");

  const validateEmail = () => {
    if (!email) {
      setEmailError("El correo electrónico es obligatorio");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("El formato del correo electrónico no es válido");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateEmail()) return;

    try {
      setIsSubmitting(true);

      const response = await recuperarClavePaso1({ email });

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.message || "Se ha enviado un PIN a tu correo electrónico",
          confirmButtonText: "Continuar",
          confirmButtonColor: "#4a5568",
        });

        // Navegar al paso 2 con el email
        navigate("/recover-password-step2", { state: { email } });
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo enviar el PIN de recuperación",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#4a5568",
        });
      }
    } catch (error: any) {
      console.error("Error en RecuperarClavePaso1:", error);

      let errorMessage = "Error al solicitar el PIN de recuperación";
      if (error?.serverMessage) {
        errorMessage = error.serverMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#4a5568",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHasPin = () => {
    navigate("/recover-password-step2", { state: { email: "" } });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-md shadow-xl animate-slideUp border rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardContent>
          <div className="flex justify-center mb-6">
            <img
              src={icons.logo}
              alt="App Logo"
              className="w-40 h-auto object-contain"
            />
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 text-center">
              Recuperar Contraseña
            </h2>
            <p className="text-sm text-gray-600 text-center mt-2">
              Paso 1 de 2
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                placeholder="usuario@easyph.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
              />
              {emailError && (
                <p className="text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-military-500 hover:bg-military-600 text-white shadow-md rounded-lg transition disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Solicitando PIN...
                </>
              ) : (
                "Solicitar PIN de recuperación"
              )}
            </Button>

            {/* <div className="text-center">
              <button
                type="button"
                onClick={handleHasPin}
                className="text-sm text-military-600 hover:text-military-500 hover:underline transition"
              >
                ¿Ya tienes un PIN de recuperación? Click aquí
              </button>
            </div> */}
          </form>
        </CardContent>

        <CardFooter className="flex justify-center">
          <Button
            variant="link"
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-military-600 flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio de sesión
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
